import { type ChangeEvent, type RefObject, useCallback, useEffect, useRef, useState } from "react";

import { getCurrentLocalTimeString, normalizeLocalTimeStringToHHmmss } from "@/utils/timeUtils";

export interface UseControlledTimeInputOptions<TStored> {
  /** Current stored value from form or external state. */
  storedValue: TStored | null | undefined;
  /** Called when a complete valid time should be committed. */
  onCommit: (value: TStored) => void;
  /** Maps stored value to local `HH:mm:ss` for display. */
  storedToDisplay: (stored: TStored) => string;
  /** Maps local `HH:mm:ss` to stored format; return null/undefined to skip commit. */
  displayToStored: (display: string) => TStored | null | undefined;
  /** When true (default), auto-populate once on mount if stored value is empty. */
  autoPopulate?: boolean;
  /** Provides display + stored pair for auto-populate; defaults to current local time. */
  getAutoPopulateValue?: () => { display: string; stored: TStored };
  /** When true, blur with empty/invalid time commits current local time instead of restoring last value. */
  resetToCurrentTimeOnBlur?: boolean;
}

export interface UseControlledTimeInputResult {
  displayValue: string;
  inputRef: RefObject<HTMLInputElement>;
  onFocus: () => void;
  onBlur: () => void;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  openPicker: () => void;
}

function isEmptyStoredValue<TStored>(value: TStored | null | undefined): boolean {
  return value === null || value === undefined || value === "";
}

/**
 * Manages a controlled Mantine/native time input with local display state during edits.
 * Commits complete local times (`HH:mm` or `HH:mm:ss`) and avoids re-syncing from stored state while focused.
 */
export function useControlledTimeInput<TStored>(
  options: UseControlledTimeInputOptions<TStored>,
): UseControlledTimeInputResult {
  const {
    storedValue,
    onCommit,
    storedToDisplay,
    displayToStored,
    autoPopulate = true,
    getAutoPopulateValue,
    resetToCurrentTimeOnBlur = false,
  } = options;

  const inputRef = useRef<HTMLInputElement>(null);
  const isEditingRef = useRef(false);
  const hasAutoPopulatedRef = useRef(false);
  const lastCommittedDisplayRef = useRef("");
  const getAutoPopulateValueRef = useRef(getAutoPopulateValue);

  getAutoPopulateValueRef.current = getAutoPopulateValue;

  const [displayValue, setDisplayValue] = useState("");

  const commitValue = useCallback(
    (display: string): boolean => {
      const stored = displayToStored(display);
      if (stored === null || stored === undefined || stored === "") {
        return false;
      }

      onCommit(stored);
      lastCommittedDisplayRef.current = display;
      return true;
    },
    [displayToStored, onCommit],
  );

  useEffect(() => {
    if (isEditingRef.current) {
      return;
    }

    if (!isEmptyStoredValue(storedValue)) {
      const display = storedToDisplay(storedValue as TStored);
      setDisplayValue(display);
      lastCommittedDisplayRef.current = display;
      return;
    }

    if (!autoPopulate || hasAutoPopulatedRef.current) {
      if (hasAutoPopulatedRef.current && isEmptyStoredValue(storedValue)) {
        return;
      }

      setDisplayValue("");
      return;
    }

    hasAutoPopulatedRef.current = true;

    const localTime = getCurrentLocalTimeString();
    const autoValue = getAutoPopulateValueRef.current?.() ?? {
      display: localTime,
      stored: displayToStored(localTime) as TStored,
    };

    if (isEmptyStoredValue(autoValue.stored)) {
      return;
    }

    onCommit(autoValue.stored);
    setDisplayValue(autoValue.display);
    lastCommittedDisplayRef.current = autoValue.display;
  }, [storedValue, storedToDisplay, displayToStored, onCommit, autoPopulate]);

  const commitDisplayValue = useCallback(
    (display: string): boolean => {
      const normalized = normalizeLocalTimeStringToHHmmss(display);
      if (!normalized) {
        return false;
      }

      setDisplayValue(normalized);

      if (normalized === lastCommittedDisplayRef.current) {
        return true;
      }

      return commitValue(normalized);
    },
    [commitValue],
  );

  const onFocus = useCallback(() => {
    isEditingRef.current = true;
  }, []);

  const onBlur = useCallback(() => {
    isEditingRef.current = false;

    const applyCurrentLocalTime = () => {
      const localTime = getCurrentLocalTimeString();
      setDisplayValue(localTime);
      commitValue(localTime);
    };

    if (!displayValue) {
      if (resetToCurrentTimeOnBlur) {
        applyCurrentLocalTime();
        return;
      }

      setDisplayValue(lastCommittedDisplayRef.current);
      return;
    }

    if (commitDisplayValue(displayValue)) {
      return;
    }

    if (resetToCurrentTimeOnBlur) {
      applyCurrentLocalTime();
      return;
    }

    setDisplayValue(lastCommittedDisplayRef.current);
  }, [displayValue, commitValue, commitDisplayValue, resetToCurrentTimeOnBlur]);

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.currentTarget;
      if (!commitDisplayValue(value)) {
        setDisplayValue(value);
      }
    },
    [commitDisplayValue],
  );

  const openPicker = useCallback(() => {
    if (typeof inputRef.current?.showPicker === "function") {
      inputRef.current.showPicker();
    }
  }, []);

  return {
    displayValue,
    inputRef,
    onFocus,
    onBlur,
    onChange,
    openPicker,
  };
}
